import { useRef, useState } from "react";

import { convertToExcalidrawElements } from "@excalidraw/element";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { useUIAppState } from "../../context/ui-appState";
import { useApp } from "../App";

import {
  convertMermaidToExcalidraw,
  insertToEditor,
  resetPreview,
} from "./common";
import { TTDDialogPanel } from "./TTDDialogPanel";
import { TTDPreviewPanel } from "./TTDPreviewPanel";

import type { BinaryFiles } from "../../types";
import type { MermaidToExcalidrawLibProps } from "./types";

const AI_SERVER_URL =
  import.meta.env.VITE_AI_SERVER_URL || "http://localhost:4000";
const GENERATE_ANYTHING_API_URL = `${AI_SERVER_URL.replace(/\/$/, "")}/generate`;
const GENERATE_ANYTHING_JSON_API_URL = `${AI_SERVER_URL.replace(
  /\/$/,
  "",
)}/generate-json`;

const requestGeneratedMermaid = async (prompt: string) => {
  // This browser call intentionally sends only the prompt to our local API.
  // Future OpenAI calls and OPENAI_API_KEY must stay in ai-server/server.js,
  // which can later run on a VPS/Docker/Portainer deployment.
  console.log("[Generate Anything] fetching:", GENERATE_ANYTHING_API_URL);

  const response = await fetch(GENERATE_ANYTHING_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) {
    throw new Error("Generate Anything request failed");
  }

  const result = await response.json();

  if (!result || typeof result.mermaid !== "string") {
    throw new Error("Generate Anything returned an invalid response");
  }

  return result.mermaid;
};

const requestGeneratedExcalidrawJson = async (
  prompt: string,
): Promise<ExcalidrawElementSkeleton[]> => {
  console.log(
    "[Generate Anything] fetching visual board:",
    GENERATE_ANYTHING_JSON_API_URL,
  );

  const response = await fetch(GENERATE_ANYTHING_JSON_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) {
    throw new Error("Generate Visual Board request failed");
  }

  const result = await response.json();

  if (
    !result ||
    result.type !== "excalidraw-json" ||
    !Array.isArray(result.elements)
  ) {
    throw new Error("Generate Visual Board returned an invalid response");
  }

  return result.elements as ExcalidrawElementSkeleton[];
};

const GenerateAnything = ({
  mermaidToExcalidrawLib,
}: {
  mermaidToExcalidrawLib: MermaidToExcalidrawLibProps;
}) => {
  const app = useApp();
  const { theme } = useUIAppState();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const data = useRef<{
    elements: readonly NonDeletedExcalidrawElement[];
    files: BinaryFiles | null;
  }>({
    elements: [],
    files: null,
  });

  const [prompt, setPrompt] = useState("");
  const [generatedMermaid, setGeneratedMermaid] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim() || !mermaidToExcalidrawLib.loaded || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedMermaid("");
    resetPreview({ canvasRef, setError });
    data.current = { elements: [], files: null };

    try {
      const mermaidDefinition = await requestGeneratedMermaid(prompt);

      const result = await convertMermaidToExcalidraw({
        canvasRef,
        data,
        mermaidToExcalidrawLib,
        setError,
        mermaidDefinition,
        theme,
      });

      if (result.success) {
        setGeneratedMermaid(mermaidDefinition);
      }
    } catch (error: any) {
      console.error("[Generate Anything] generation failed", {
        url: GENERATE_ANYTHING_API_URL,
        prompt,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateVisualBoard = async () => {
    if (!prompt.trim() || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedMermaid("");
    resetPreview({ canvasRef, setError });
    data.current = { elements: [], files: null };

    try {
      console.log("[Generate Anything] generating visual board");
      const elements = await requestGeneratedExcalidrawJson(prompt);

      data.current = {
        elements: convertToExcalidrawElements(elements, {
          regenerateIds: true,
        }) as readonly NonDeletedExcalidrawElement[],
        files: null,
      };

      console.log("[Generate Anything] inserting visual board", {
        elements: data.current.elements.length,
      });
      insertToEditor({ app, data });
    } catch (error: any) {
      console.error("[Generate Anything] visual board generation failed", {
        url: GENERATE_ANYTHING_JSON_API_URL,
        prompt,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInsertToEditor = () => {
    insertToEditor({ app, data });
  };

  return (
    <div className="ttd-dialog-layout ttd-dialog-layout--split">
      <TTDDialogPanel
        className="ttd-dialog-chat-panel"
        panelActions={[
          {
            action: handleGenerate,
            label: "Generate Diagram",
            variant: "button",
            disabled:
              !prompt.trim() || !mermaidToExcalidrawLib.loaded || isGenerating,
          },
          {
            action: handleGenerateVisualBoard,
            label: "Generate Visual Board",
            variant: "button",
            disabled: !prompt.trim() || isGenerating,
          },
        ]}
        onTextSubmitInProgess={isGenerating}
      >
        <div className="chat-interface">
          <div className="chat-interface__messages">
            {prompt && (
              <div className="chat-message chat-message--user">
                <div className="chat-message__content">
                  <div className="chat-message__body chat-message__text">
                    {prompt}
                  </div>
                </div>
              </div>
            )}
            {generatedMermaid && (
              <div className="chat-message chat-message--assistant">
                <div className="chat-message__content">
                  <div className="chat-message__body chat-message__text">
                    {generatedMermaid}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="chat-interface__input-container">
            <div className="chat-interface__input-outer">
              <div className="chat-interface__input-wrapper">
                <textarea
                  className="chat-interface__input"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder="Describe anything to generate..."
                  rows={1}
                />
              </div>
            </div>
          </div>
        </div>
      </TTDDialogPanel>
      <TTDPreviewPanel
        canvasRef={canvasRef}
        error={error}
        loaded={mermaidToExcalidrawLib.loaded}
        onInsert={handleInsertToEditor}
      />
    </div>
  );
};

export default GenerateAnything;
